/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { widgetsCreate } from "../funcs/widgetsCreate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Widget } from "../models/components/widget.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateWidgetRequest,
  CreateWidgetSecurity,
} from "../models/operations/createwidget.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateWidgetMutationVariables = {
  request: CreateWidgetRequest;
  security?: CreateWidgetSecurity | undefined;
  options?: RequestOptions;
};

export type CreateWidgetMutationData = Widget;

export type CreateWidgetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createWidget widgets
 *
 * @remarks
 * Save a widget. Any member of the project can. The question is validated against the catalog, and the chart against the question, before it is stored.
 */
export function useCreateWidgetMutation(
  options?: MutationHookOptions<
    CreateWidgetMutationData,
    CreateWidgetMutationError,
    CreateWidgetMutationVariables
  >,
): UseMutationResult<
  CreateWidgetMutationData,
  CreateWidgetMutationError,
  CreateWidgetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateWidgetMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateWidget(): MutationKey {
  return ["@gram/client", "widgets", "create"];
}

export function buildCreateWidgetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateWidgetMutationVariables,
  ) => Promise<CreateWidgetMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateWidget(),
    mutationFn: function createWidgetMutationFn({
      request,
      security,
      options,
    }): Promise<CreateWidgetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(widgetsCreate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
